/**
 * Filtered List Block JavaScript - AJAX Version
 *
 * Handles AJAX search, filtering, sorting, and pagination with removable filter pills.
 *
 * @param {boolean} [enableDebug=false] - Enable debug logging
 */
export default function (enableDebug = false) {
  const instances = document.querySelectorAll('.filtered-list');

  if (enableDebug)
    console.log(`[FilteredList] Found ${instances.length} instance(s)`);
  if (instances.length === 0) return;

  instances.forEach(function (instance, instanceIndex) {
    if (enableDebug)
      console.log(`[FilteredList] Initializing instance ${instanceIndex + 1}`);

    const blockName = 'filtered-list';
    const contentWrapper = instance.querySelector(
      `.${blockName}__content-list`,
    );
    const layout = contentWrapper?.getAttribute('data-layout') || 'default';
    const postType =
      contentWrapper
        ?.getAttribute('data-post-type')
        ?.split(',')
        .map((type) => type.trim())
        .filter(Boolean) || [];

    // Control elements
    const searchForm = instance.querySelector('.filtered-list-search-form');
    const searchInput = instance.querySelector('.filtered-list-search-input');
    const paginationWrapper = instance.querySelector(
      `.${blockName}__pagination`,
    );
    const paginationList = instance.querySelector(
      `.${blockName}__pagination-list`,
    );
    const resultsCounter = instance.querySelector(
      `.${blockName}__results-count`,
    );
    const activeFiltersWrapper = instance.querySelector(
      `.${blockName}__active-filters`,
    );
    const activeFiltersList = instance.querySelector(
      `.${blockName}__active-filters-list`,
    );
    const removeAllFiltersButton = instance.querySelector(
      `.${blockName}__remove-all-filters`,
    );

    // Filter elements - get all dynamically
    const filterSelects = Array.from(
      instance.querySelectorAll(`.${blockName}__filter-select`),
    );
    const sortSelect = instance.querySelector(
      `.${blockName}__filter-dropdown.--sorter select`,
    );

    // Configuration
    const postsPerPage =
      parseInt(contentWrapper?.getAttribute('data-posts-paged'), 10) || 10;

    // State management
    let currentPage = 1;
    let currentFilters = {};
    let currentSearch = '';
    let currentSort = '1';
    let isLoading = false;
    let currentParentStandardTerm =
      contentWrapper?.getAttribute('data-parent-standard-term') || '';
    let showAllStandards =
      contentWrapper?.getAttribute('data-show-all-standards') === '1' || false;

    // Initialize pre-filter for events (if set in admin)
    const eventTypePrefilter =
      contentWrapper?.getAttribute('data-event-type-prefilter') || '';
    if (eventTypePrefilter) {
      currentFilters['event-type'] = eventTypePrefilter;
    }

    // Initialize pre-filter for advocacy-type (if set in admin block config)
    const advocacyTypePrefilter =
      contentWrapper?.getAttribute('data-advocacy-type') || '';

    if (paginationWrapper) {
      paginationWrapper.addEventListener('click', function (e) {
        const link = e.target.closest(`.${blockName}__pagination-link`);
        if (!link) return;

        e.preventDefault();

        // Skip disabled links
        const li = link.closest(`.${blockName}__pagination-list-item`);
        if (li && li.classList.contains('--disabled')) return;

        // Determine which page to load
        let page;
        if (link.classList.contains('--start-page')) {
          page = 1;
        } else if (link.classList.contains('--previous-page')) {
          page = Math.max(1, currentPage - 1);
        } else if (link.classList.contains('--next-page')) {
          page = currentPage + 1;
        } else if (link.classList.contains('--end-page')) {
          // Find the last numbered page link
          const numberedLinks = paginationWrapper.querySelectorAll(
            `.${blockName}__pagination-link:not(.--start-page):not(.--previous-page):not(.--next-page):not(.--end-page)`,
          );
          const lastLink = numberedLinks[numberedLinks.length - 1];
          page = lastLink ? parseInt(lastLink.textContent, 10) : currentPage;
        } else {
          page = parseInt(link.textContent, 10);
        }

        if (page && !isNaN(page)) {
          fetchFilteredPosts(page);
          instance.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    /**
     * Make AJAX request to get filtered posts
     */
    function fetchFilteredPosts(page = 1, updateUrl = true) {
      if (isLoading) return;
      isLoading = true;

      // Show loading state
      contentWrapper.style.opacity = '0.5';
      contentWrapper.style.pointerEvents = 'none';

      updateCurrentFiltersFromSelects();

      if (eventTypePrefilter) {
        currentFilters['event-type'] = eventTypePrefilter;
      }

      // Get employee_type from data attribute if it exists
      const employeeType =
        contentWrapper?.getAttribute('data-employee-type') || '';

      const data = {
        action: 'filtered_list',
        nonce: wpAjax?.nonce || '',
        post_type: postType,
        search: currentSearch,
        filters: JSON.stringify(currentFilters),
        sort: currentSort,
        page: page,
        posts_per_page: postsPerPage,
        employee_type: employeeType,
        parent_standard_term: currentParentStandardTerm,
        show_all_standards: showAllStandards ? 1 : 0,
        advocacy_type: advocacyTypePrefilter,
      };

      // Use jQuery AJAX (which is available in WordPress)
      $.post(wpAjax.ajaxurl, data)
        .done(function (response) {
          if (enableDebug)
            console.log('[FilteredList] AJAX response:', response);

          if (response.success) {
            currentPage = page;

            if (
              typeof response.data.parent_standard_term !== 'undefined' &&
              response.data.parent_standard_term !== null
            ) {
              currentParentStandardTerm = String(
                response.data.parent_standard_term,
              );
              if (contentWrapper) {
                contentWrapper.setAttribute(
                  'data-parent-standard-term',
                  currentParentStandardTerm,
                );
              }
            }

            try {
              renderPosts(
                response.data.posts,
                response.data.layout,
                response.data,
              );
            } catch (e) {
              console.error('[FilteredList] Error rendering posts:', e);
            }
            updateResultsCounter(response.data.total_posts);
            renderPagination(
              response.data.current_page,
              response.data.total_pages,
            );
            updateActiveFilters();
            refreshAllFauxPopupsInInstance();
          } else {
            console.error('[FilteredList] AJAX error:', response);
          }
        })
        .fail(function (xhr, status, error) {
          console.error(
            '[FilteredList] Request failed:',
            error,
            xhr.responseText,
          );
        })
        .always(function () {
          isLoading = false;
          contentWrapper.style.opacity = '';
          contentWrapper.style.pointerEvents = '';
        });
    }

    /**
     * Render posts in the content wrapper
     */
    function renderPosts(posts, layout, responseData = {}) {
      if (!contentWrapper) return;

      if (layout === 'standards') {
        // Check if we're in All mode or Grouped mode
        const isAllMode = responseData.is_standards_all_mode || false;
        const isGroupedMode = responseData.is_standards_grouped_mode || false;

        if (isAllMode) {
          // All mode: render as standard ungrouped list
          if (!posts || posts.length === 0) {
            contentWrapper.innerHTML = `
							<div class="${blockName}__no-content">
								<p>No content found.</p>
							</div>
						`;
            return;
          }

          contentWrapper.innerHTML = posts
            .map(function (post, index) {
              return generateStandardsItemHTML(post, layout, index + 1);
            })
            .join('');
        } else if (isGroupedMode) {
          // Grouped mode: render grouped layout
          const standardsData = responseData.standards_data || {};
          contentWrapper.innerHTML = renderStandardsLayout(standardsData);
          initializeStandardsGroups();
        } else {
          // Fallback for old data without mode flags
          const standardsData = responseData.standards_data || {};
          contentWrapper.innerHTML = renderStandardsLayout(standardsData);
          initializeStandardsGroups();
        }
      } else if (layout === 'tcrp') {
        // TCRP Publications layout - always flat list with pagination
        if (!posts || posts.length === 0) {
          contentWrapper.innerHTML = `
            <div class="${blockName}__no-content">
              <p>No TCRP publications found.</p>
            </div>
          `;
          return;
        }

        contentWrapper.innerHTML = posts
          .map(function (post, index) {
            return generateTCRPItemHTML(post, index + 1);
          })
          .join('');
      } else if (layout === 'documents') {
        // Document Library layout - flat list with pagination
        if (!posts || posts.length === 0) {
          contentWrapper.innerHTML = `
            <div class="${blockName}__no-content">
              <p>No documents found.</p>
            </div>
          `;
          return;
        }

        contentWrapper.innerHTML = posts
          .map(function (post, index) {
            return generateDocumentItemHTML(post, index + 1);
          })
          .join('');
      } else {
        if (!posts || posts.length === 0) {
          contentWrapper.innerHTML = `
						<div class="${blockName}__no-content">
							<p>No content found.</p>
						</div>
					`;
          return;
        }

        contentWrapper.innerHTML = posts
          .map(function (post, index) {
            return generatePostHTML(post, layout, index + 1);
          })
          .join('');
      }

      if (['awards', 'people', 'cards'].includes(layout)) {
        initializeExpandCollapse();
      }

      if (layout === 'tcrp') {
        hideTCRPToggleIfNotOverflowing();
      }

      if (layout === 'documents') {
        hideDocumentToggleIfNotOverflowing();
      }
    }

    const HTML_ESCAPE_LOOKUP = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
      '`': '&#096;',
    };

    function escapeHTML(value) {
      if (value === null || value === undefined) {
        return '';
      }

      return String(value).replace(
        /[&<>"'`]/g,
        (character) => HTML_ESCAPE_LOOKUP[character] || character,
      );
    }

    /**
     * Generate HTML for a single post based on layout
     */
    function generatePostHTML(post, layout, index) {
      // Generate taxonomy attributes
      let taxonomyAttrs = '';
      if (post.taxonomies) {
        Object.keys(post.taxonomies).forEach(function (taxonomy) {
          const terms = post.taxonomies[taxonomy];
          if (terms && terms.length > 0) {
            const termSlugs = terms.map((term) => term.slug).join(',');
            taxonomyAttrs += ` data-${taxonomy}="${termSlugs}"`;
          }
        });
      }

      const blockId = instance.id || 'filtered-list';

      function getLinkClass(url) {
        const trustedDomains = Array.isArray(window.filteredListTrustedDomains)
          ? window.filteredListTrustedDomains.map((d) => d.toLowerCase())
          : [];
        const currentDomain = window.location.hostname.toLowerCase();
        if (!url) return '';
        if (/^(mailto:|tel:)/i.test(url)) return 'whitelist-domain-link';
        if (/^\/(?!\/)/.test(url)) return 'whitelist-domain-link';
        try {
          const link = new URL(url, window.location.origin);
          const linkDomain = link.hostname.toLowerCase();
          if (linkDomain === currentDomain) return 'whitelist-domain-link';
          if (trustedDomains.some((domain) => linkDomain.endsWith(domain))) return 'whitelist-domain-link';
          return 'blacklist-domain-link';
        } catch (e) {
          return 'whitelist-domain-link';
        }
      }

      switch (layout) {
        case 'press':
          return `
						<article class="${blockName}__item ${blockName}__item--press"
								 data-item-index="${index}"
								 data-search-terms="${post.search_terms}">
							<div class="${blockName}__item-content">
								<div class="${blockName}__item-thumbnail">
									<img src="${
                    post.featured_image || 'https://placehold.co/456x320'
                  }" alt="" class="${blockName}__item-image" />
								</div>
								<div class="${blockName}__item-details">
									${
                    post.tag_label
                      ? `<div class="${blockName}__item-tag">${post.tag_label}</div>`
                      : ''
                  }
									<h3 class="${blockName}__item-title"><a href="${post.permalink}">${post.title}</a></h3>
									${post.date ? `<div class="${blockName}__item-date">${post.date}</div>` : ''}
									${
                    post.excerpt
                      ? `<div class="${blockName}__item-excerpt">${post.excerpt}</div>`
                      : ''
                  }
									<div class="${blockName}__item-action">
										<a href="${
                      post.permalink
                    }" class="button--primary" title="Read More" target="_self">Read More</a>
									</div>
								</div>
							</div>
						</article>
					`;

        case 'calendar':
          return `
						<article class="${blockName}__item ${blockName}__item--event"
								 data-item-index="${index}"
								 data-search-terms="${post.search_terms}"
								 data-start-date="${post.start_date || ''}"
								 ${taxonomyAttrs}>
							<div class="${blockName}__item-content">
								<div class="${blockName}__item-meta">
									${
                    post.formatted_date
                      ? `<div class="${blockName}__item-date"><time datetime="${post.start_date}">${post.formatted_date}</time></div>`
                      : ''
                  }
									${
                    post.formatted_time
                      ? `<div class="${blockName}__item-time">${post.formatted_time}</div>`
                      : ''
                  }
								</div>
								<div class="${blockName}__item-details">
									<h3 class="${blockName}__item-title"><a href="${post.permalink}">${post.title}</a></h3>
									${
                    post.location
                      ? `<div class="${blockName}__item-location">${post.location}</div>`
                      : ''
                  }
									${
                    post.excerpt
                      ? `<div class="${blockName}__item-excerpt">${post.excerpt}</div>`
                      : ''
                  }
								</div>
								${
                  post.has_action_url
                    ? `
									<div class="${blockName}__item-action">
										<a href="${post.registration_url}" class="${blockName}__action-button" target="_blank" rel="noopener">
											Take Action
										</a>
									</div>
								`
                    : ''
                }
							</div>
						</article>
					`;

        case 'cards':
          return `
						<div class="${blockName}__card"
							 data-item-index="${index}"
							 data-search-terms="${post.search_terms}"
							 data-firstname="${post.staff_full_name || ''}"
							 data-title="${post.staff_title || ''}"
							 ${taxonomyAttrs}>
							<div class="${blockName}__card-content">
								${
                  post.profile_picture
                    ? `
									<div class="${blockName}__card-thumbnail">
										<img src="${post.profile_picture.sizes?.medium || post.profile_picture.url}"
											 class="${blockName}__card-image"
											 alt="${post.profile_picture.alt || post.staff_full_name}" />
									</div>
								`
                    : ''
                }
								<div class="${blockName}__card-details">
									${
                    post.apta_title
                      ? `<div class="${blockName}__card-apta-title">${post.apta_title}</div>`
                      : ''
                  }
									${
                    post.staff_full_name
                      ? `<h3 class="${blockName}__card-title">${post.staff_full_name}</h3>`
                      : ''
                  }
									${
                    post.staff_title
                      ? `<div class="${blockName}__card-staff-title">${post.staff_title}</div>`
                      : ''
                  }
									${
                    post.location
                      ? `<div class="${blockName}__card-location">${post.location}</div>`
                      : ''
                  }
									${
                    post.has_bio
                      ? `
										<button class="${blockName}__card-toggle"
												data-text-more="Read More"
												data-text-less="Read Less"
												aria-expanded="false"
												aria-controls="staff-bio-${index}">
											Read More
										</button>
										<div id="staff-bio-${index}"
											 class="${blockName}__card-content-extended">
											<div class="${blockName}__card-full-bio">${post.bio}</div>
										</div>
									`
                      : ''
                  }
								</div>
							</div>
						</div>
					`;

        case 'awards':
          return `
						<article class="${blockName}__item ${blockName}__item--person"
								 data-item-index="${index}"
								 data-search-terms="${post.search_terms}"
								 data-firstname="${(post.full_name || '').toLowerCase()}"
								 data-year="${post.award_year || post.year_for_sort || ''}"
								 ${taxonomyAttrs}>
							<div class="${blockName}__item-content">
								${
                  post.image
                    ? `
									<div class="${blockName}__item-thumbnail">
										<img src="${post.image.sizes?.medium || post.image.url}"
											 class="${blockName}__item-image"
											 alt="${post.image.alt || post.full_name}" />
									</div>
								`
                    : ''
                }
								<div class="${blockName}__item-details">
									${
                    post.full_name
                      ? `<h3 class="${blockName}__item-title">${post.full_name}</h3>`
                      : ''
                  }
									${
                    post.award_year
                      ? `<div class="${blockName}__item-year">${post.award_year}</div>`
                      : ''
                  }
									${
                    post.award_name
                      ? `<div class="${blockName}__item-award-name">${post.award_name}</div>`
                      : ''
                  }
									${
                    post.organization
                      ? `<div class="${blockName}__item-organization">${post.organization}</div>`
                      : ''
                  }
									${
                    post.has_bio
                      ? `
										<button class="${blockName}__item-toggle"
												data-text-more="Read More"
												data-text-less="Read Less"
												aria-expanded="false"
												aria-controls="award-bio-${index}">
											Read More
										</button>
										<div id="award-bio-${index}"
											 class="${blockName}__item-content-extended">
											<div class="${blockName}__item-bio-long">${post.biography}</div>
										</div>
									`
                      : ''
                  }
								</div>
							</div>
						</article>
					`;

        case 'legislative':
          return `
						<article class="${blockName}__item ${blockName}__item--legislative"
								 data-item-index="${index}"
								 data-search-terms="${post.search_terms}"
								 data-publication-date="${post.publication_date || ''}"
								 ${taxonomyAttrs}>
							<div class="${blockName}__item-content">
								<div class="${ blockName }__item-icon" aria-hidden="true">
									<svg width="29" height="30" viewBox="0 0 29 30" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.80551 9.41665C8.18983 9.41665 8.52257 9.26442 8.80374 8.95997C9.08464 8.65522 9.22508 8.29456 9.22508 7.87801C9.22508 7.46115 9.08436 7.1005 8.80291 6.79605C8.52174 6.4913 8.19066 6.33892 7.80967 6.33892C7.4223 6.33892 7.08817 6.49145 6.80727 6.7965C6.5261 7.10155 6.38552 7.46055 6.38552 7.8735C6.38552 8.29306 6.5261 8.65522 6.80727 8.95997C7.08817 9.26442 7.42091 9.41665 7.80551 9.41665ZM7.80551 16.5346C8.18761 16.5346 8.5198 16.3837 8.80208 16.082C9.08408 15.7802 9.22508 15.4196 9.22508 15C9.22508 14.5804 9.08436 14.2183 8.80291 13.9135C8.52174 13.6091 8.19066 13.4569 7.80967 13.4569C7.4223 13.4569 7.08817 13.6091 6.80727 13.9135C6.5261 14.2183 6.38552 14.5804 6.38552 15C6.38552 15.4196 6.52666 15.7802 6.80894 16.082C7.09122 16.3837 7.42341 16.5346 7.80551 16.5346ZM7.80551 23.6611C8.18983 23.6611 8.52257 23.5087 8.80374 23.2039C9.08464 22.8995 9.22508 22.5388 9.22508 22.122C9.22508 21.7054 9.08436 21.3448 8.80291 21.04C8.52174 20.7356 8.19066 20.5834 7.80967 20.5834C7.4223 20.5834 7.08817 20.7359 6.80727 21.0409C6.5261 21.3457 6.38552 21.7045 6.38552 22.1175C6.38552 22.5373 6.5261 22.8995 6.80727 23.2039C7.08817 23.5087 7.42091 23.6611 7.80551 23.6611ZM3.37293 30C2.58682 30 1.91772 29.7007 1.36564 29.102C0.813288 28.5036 0.537109 27.7784 0.537109 26.9263V3.07366C0.537109 2.22162 0.813288 1.49641 1.36564 0.898025C1.91772 0.299341 2.5871 0 3.37376 0H20.5451L28.2157 8.31395V26.9254C28.2157 27.7781 27.9395 28.5036 27.3871 29.102C26.8351 29.7007 26.166 30 25.3799 30H3.37293ZM3.37667 27.4772H25.3761C25.5256 27.4772 25.6483 27.4252 25.7442 27.3213C25.8402 27.2173 25.8881 27.0843 25.8881 26.9223V9.66549H19.2981V2.52276H3.37667C3.22721 2.52276 3.10451 2.57476 3.00857 2.67875C2.91263 2.78274 2.86466 2.91573 2.86466 3.07772V26.9223C2.86466 27.0843 2.91263 27.2173 3.00857 27.3213C3.10451 27.4252 3.22721 27.4772 3.37667 27.4772Z" fill="#6049E9"/></svg>

								</div>
                <div class="${blockName}__item-details">
                  <div class="${blockName}__item-main-information">
                    <h3 class="${blockName}__item-title"><a href="${post.permalink}">${post.title}</a></h3>
                    ${
                      post.date
                        ? `<div class="${blockName}__item-date">${post.date}</div>`
                        : ''
                    }
                    ${
                      post.source
                        ? `<div class="${blockName}__item-source">${post.source}</div>`
                        : ''
                    }
                  </div>
                  ${
                    post.has_action_url
                      ? `
                    <div class="${blockName}__item-action">
                      <a href="${post.action_url}" class="${blockName}__action-button" target="_blank" rel="noopener">
                        View Document
                      </a>
                    </div>
                  `
                      : ''
                  }
                  </div>
							</div>
						</article>
					`;

        case 'events':
          return `
						<div class="event-card${
              post.featured_image ? '' : ' no-media'
            }" data-event-id="${post.ID}" ${taxonomyAttrs}>
							<div class="event-card__grid">
								<div class="event-card__grid--inner">
									${
                    post.featured_image
                      ? `
										<div class="event-card__media">
											<img src="${post.featured_image}" alt="" class="event-card__thumbnail" />
										</div>
									`
                      : ''
                  }
									<div class="event-card__details">
										<h3 class="event-card__title">
											<a href="${post.permalink}">${post.alt_title || post.post_title || post.title }</a>
										</h3>
										<div class="event-card__datetime">
											${
                        post.event_end_date &&
                        post.event_end_date_formatted &&
                        post.event_end_date_formatted !==
                          post.event_date_formatted
                          ? `
												<span class="event-card__date">${post.event_date_formatted} - ${
                              post.event_end_date_formatted
                            }</span>
												${post.display_event_times ? `<span class="event-card__time"><span>${post.event_time_formatted}${
                              post.event_end_time_formatted &&
                              post.event_end_time_formatted !==
                                post.event_time_formatted
                                ? ` - ${post.event_end_time_formatted}`
                                : ''
                            }</span> ${
                              post.timezone ? post.timezone : ''
                            }</span>` : (post.custom_event_time ? `<span class="event-card__time">${post.custom_event_time}</span>` : '')}
											`
                          : `
												<span class="event-card__date">${post.event_date_formatted || ''}</span>
												${post.display_event_times ? `<span class="event-card__time"><span>${
                          post.event_time_formatted || ''
                        }</span> ${post.timezone ? post.timezone : ''}</span>` : (post.custom_event_time ? `<span class="event-card__time">${post.custom_event_time}</span>` : '')}
											`
                      }
										</div>
										${
                      post.location
                        ? `<div class="event-card__location">${post.location}</div>`
                        : ''
                    }
									</div>
									${
                    post.registration_link
                      ? `
										<div class="event-card__cta">
											<div class="event-card__cta--inner">
												<a href="${post.registration_link}" title="Register" class="event-card__button" target="_blank" data-event-id="${post.ID}">
													<label>Register</label>
													<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" stroke-width="2" fill="none"/></svg>
												</a>
											</div>
										</div>
									`
                      : ''
                  }
								</div>
							</div>
						</div>
					`;

        case 'hof':
          return `
				<article class="${blockName}__item ${blockName}__item--person"
						data-item-index="${index}"
						data-search-terms="${post.search_terms}"
						data-firstname="${post.first_name ? post.first_name.toLowerCase() : ''}"
						data-lastname="${post.last_name ? post.last_name.toLowerCase() : ''}"
						data-year="${post.award_year ?? post.year_for_sort ?? ''}"
						${taxonomyAttrs}>

					<div class="${blockName}__hof-items-center">
						<div class="${blockName}__item-thumbnail ${post.featured_image ? '' : '--placeholder'}">
							${
                post.featured_image
                  ? `
								<img src="${post.featured_image}"
									class="${blockName}__item-image"
									alt="${post.image?.alt ? post.image.alt : post.name}" />`
                  : ''
              }
						</div>

						<div class="${blockName}__hof-item-details">
							<h4 class="${blockName}__hof-item-title">${post.name}</h4>

							${
                post.award_year
                  ? `
								<div class="${blockName}__hof-item-year">${post.award_year}</div>
							`
                  : ''
              }

							${
                post.award_name
                  ? `
								<div class="${blockName}__item-award-name">${post.award_name}</div>
							`
                  : ''
              }

							${
                post.organization
                  ? `
								<div class="${blockName}__item-organization">${post.organization}</div>
							`
                  : ''
              }

							${
                post.body_content_full
                  ? `
								<div id="filtered-list-bio-${blockId}-${index}"
									class="${blockName}__item-content-extended">

									<div class="${blockName}__hof-item-bio-long ${
                      post.body_content_trimmed?.length >= 238
                        ? '--expandable'
                        : ''
                    }">
										${post.body_content_full}
									</div>

									${
                    post.body_content_trimmed?.length >= 237
                      ? `
										<button class="${blockName}__item-toggle hof-primary-link"
												data-text-more="Read More"
												data-text-less="Read Less"
												aria-expanded="false">
											Read More
										</button>`
                      : ''
                  }
								</div>
							`
                  : ''
              }
						</div>
					</div>
				</article>`;

        case 'resources':
          return `
									<article class="${blockName}__item ${blockName}__item--resource"
											 data-item-index="${index}"
											 data-search-terms="${post.search_terms}"
											 ${taxonomyAttrs}>
										<div class="${blockName}__item-content">
											<div class="${blockName}__item-icon" aria-hidden="true">
												<svg width="29" height="30" viewBox="0 0 29 30" fill="none" xmlns="http://www.w3.org/2000/svg">
													<path d="M7.80551 9.41665C8.18983 9.41665 8.52257 9.26442 8.80374 8.95997C9.08464 8.65522 9.22508 8.29456 9.22508 7.87801C9.22508 7.46115 9.08436 7.1005 8.80291 6.79605C8.52174 6.4913 8.19066 6.33892 7.80967 6.33892C7.4223 6.33892 7.08817 6.49145 6.80727 6.7965C6.5261 7.10155 6.38552 7.46055 6.38552 7.8735C6.38552 8.29306 6.5261 8.65522 6.80727 8.95997C7.08817 9.26442 7.42091 9.41665 7.80551 9.41665ZM7.80551 16.5346C8.18761 16.5346 8.5198 16.3837 8.80208 16.082C9.08408 15.7802 9.22508 15.4196 9.22508 15C9.22508 14.5804 9.08436 14.2183 8.80291 13.9135C8.52174 13.6091 8.19066 13.4569 7.80967 13.4569C7.4223 13.4569 7.08817 13.6091 6.80727 13.9135C6.5261 14.2183 6.38552 14.5804 6.38552 15C6.38552 15.4196 6.52666 15.7802 6.80894 16.082C7.09122 16.3837 7.42341 16.5346 7.80551 16.5346ZM7.80551 23.6611C8.18983 23.6611 8.52257 23.5087 8.80374 23.2039C9.08464 22.8995 9.22508 22.5388 9.22508 22.122C9.22508 21.7054 9.08436 21.3448 8.80291 21.04C8.52174 20.7356 8.19066 20.5834 7.80967 20.5834C7.4223 20.5834 7.08817 20.7359 6.80727 21.0409C6.5261 21.3457 6.38552 21.7045 6.38552 22.1175C6.38552 22.5373 6.5261 22.8995 6.80727 23.2039C7.08817 23.5087 7.42091 23.6611 7.80551 23.6611ZM3.37293 30C2.58682 30 1.91772 29.7007 1.36564 29.102C0.813288 28.5036 0.537109 27.7784 0.537109 26.9263V3.07366C0.537109 2.22162 0.813288 1.49641 1.36564 0.898025C1.91772 0.299341 2.5871 0 3.37376 0H20.5451L28.2157 8.31395V26.9254C28.2157 27.7781 27.9395 28.5036 27.3871 29.102C26.8351 29.7007 26.166 30 25.3799 30H3.37293ZM3.37667 27.4772H25.3761C25.5256 27.4772 25.6483 27.4252 25.7442 27.3213C25.8402 27.2173 25.8881 27.0843 25.8881 26.9223V9.66549H19.2981V2.52276H3.37667C3.22721 2.52276 3.10451 2.57476 3.00857 2.67875C2.91263 2.78274 2.86466 2.91573 2.86466 3.07772V26.9223C2.86466 27.0843 2.91263 27.2173 3.00857 27.3213C3.10451 27.4252 3.22721 27.4772 3.37667 27.4772Z" fill="#6049E9"/>
												</svg>
											</div>
											<div class="${blockName}__item-details">
												<div class="filtered-list__item-main-information">

													<h3 class="${blockName}__item-title"><a href="${post.permalink}" class="${getLinkClass(post.permalink)}">${post.title}</a></h3>
													${
                            post.date
                              ? `
															<div class="${blockName}__item-date">${post.date}</div>
															`
                              : ''
                          }
												</div>

												${
                          post.has_action_url
                            ? `
														<a href="${escapeHTML(
                              post.action_url,
                            )}" class="download button--primary ${getLinkClass(
                                post.action_url,
                              )}" target="_blank" rel="noopener">
																		Download File
																		<svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"><rect y="0.5" width="14" height="14" rx="2" fill="#6049E9"/><path d="M3.5 6.48211L3.9887 5.97469L6.64453 8.64791L6.64453 3L7.34604 3L7.34604 8.64791L10.0115 5.9652L10.5 6.47247L6.99536 10L3.5 6.48211Z" fill="white"/><rect x="3.5" y="11" width="7" height="0.583333" fill="white"/></svg>
																		${
                                      post.file_type_label
                                        ? `<span>(${escapeHTML(
                                            post.file_type_label,
                                          )})</span>`
                                        : ''
                                    }
																	</a>
														`
                            : ''
                        }
											</div>

										</div>
									</article>
								`;
        case 'legislative':
          return `
						<article class="${blockName}__item ${blockName}__item--legislative"
								 data-item-index="${index}"
								 data-search-terms="${post.search_terms}"
								 data-publication-date="${post.publication_date || ''}"
								 ${taxonomyAttrs}>
							<div class="${blockName}__item-content">
								<div class="${blockName}__item-icon">
									<svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M30 5L50 15V20H10V15L30 5Z" fill="#6049E9"/>
										<rect x="15" y="25" width="5" height="20" fill="#6049E9"/>
										<rect x="27.5" y="25" width="5" height="20" fill="#6049E9"/>
										<rect x="40" y="25" width="5" height="20" fill="#6049E9"/>
										<rect x="10" y="50" width="40" height="5" fill="#6049E9"/>
									</svg>
								</div>
								<div class="${blockName}__item-details">
									<h3 class="${blockName}__item-title"><a href="${post.permalink}">${post.title}</a></h3>
									${
                    post.formatted_date
                      ? `<div class="${blockName}__item-date">${post.formatted_date}</div>`
                      : ''
                  }
									${
                    post.source
                      ? `<div class="${blockName}__item-source">${post.source}</div>`
                      : ''
                  }
									${
                    post.excerpt
                      ? `<div class="${blockName}__item-excerpt">${post.excerpt}</div>`
                      : ''
                  }
								</div>
								${
                  post.has_action_url
                    ? `
									<div class="${blockName}__item-action">
										<a href="${post.action_url}" class="${blockName}__action-button" target="_blank" rel="noopener">
											View Document
										</a>
									</div>
								`
                    : ''
                }
							</div>
						</article>
					`;

        case 'federal-comments':
          return `
						<article class="${blockName}__item ${blockName}__item--federal-comment"
								 data-item-index="${index}"
								 data-search-terms="${escapeHTML(post.search_terms || '')}"
								 data-agency="${escapeHTML(post.agency_slug || '')}">
							<div class="${blockName}__item-content">
								<div class="${blockName}__item-icon" aria-hidden="true">
									<svg width="29" height="30" viewBox="0 0 29 30" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.80551 9.41665C8.18983 9.41665 8.52257 9.26442 8.80374 8.95997C9.08464 8.65522 9.22508 8.29456 9.22508 7.87801C9.22508 7.46115 9.08436 7.1005 8.80291 6.79605C8.52174 6.4913 8.19066 6.33892 7.80967 6.33892C7.4223 6.33892 7.08817 6.49145 6.80727 6.7965C6.5261 7.10155 6.38552 7.46055 6.38552 7.8735C6.38552 8.29306 6.5261 8.65522 6.80727 8.95997C7.08817 9.26442 7.42091 9.41665 7.80551 9.41665ZM7.80551 16.5346C8.18761 16.5346 8.5198 16.3837 8.80208 16.082C9.08408 15.7802 9.22508 15.4196 9.22508 15C9.22508 14.5804 9.08436 14.2183 8.80291 13.9135C8.52174 13.6091 8.19066 13.4569 7.80967 13.4569C7.4223 13.4569 7.08817 13.6091 6.80727 13.9135C6.5261 14.2183 6.38552 14.5804 6.38552 15C6.38552 15.4196 6.52666 15.7802 6.80894 16.082C7.09122 16.3837 7.42341 16.5346 7.80551 16.5346ZM7.80551 23.6611C8.18983 23.6611 8.52257 23.5087 8.80374 23.2039C9.08464 22.8995 9.22508 22.5388 9.22508 22.122C9.22508 21.7054 9.08436 21.3448 8.80291 21.04C8.52174 20.7356 8.19066 20.5834 7.80967 20.5834C7.4223 20.5834 7.08817 20.7359 6.80727 21.0409C6.5261 21.3457 6.38552 21.7045 6.38552 22.1175C6.38552 22.5373 6.5261 22.8995 6.80727 23.2039C7.08817 23.5087 7.42091 23.6611 7.80551 23.6611ZM3.37293 30C2.58682 30 1.91772 29.7007 1.36564 29.102C0.813288 28.5036 0.537109 27.7784 0.537109 26.9263V3.07366C0.537109 2.22162 0.813288 1.49641 1.36564 0.898025C1.91772 0.299341 2.5871 0 3.37376 0H20.5451L28.2157 8.31395V26.9254C28.2157 27.7781 27.9395 28.5036 27.3871 29.102C26.8351 29.7007 26.166 30 25.3799 30H3.37293ZM3.37667 27.4772H25.3761C25.5256 27.4772 25.6483 27.4252 25.7442 27.3213C25.8402 27.2173 25.8881 27.0843 25.8881 26.9223V9.66549H19.2981V2.52276H3.37667C3.22721 2.52276 3.10451 2.57476 3.00857 2.67875C2.91263 2.78274 2.86466 2.91573 2.86466 3.07772V26.9223C2.86466 27.0843 2.91263 27.2173 3.00857 27.3213C3.10451 27.4252 3.22721 27.4772 3.37667 27.4772Z" fill="#6049E9"/></svg>
								</div>
								<div class="${blockName}__item-details">
									<div class="${blockName}__item-main-information">
										<h3 class="${blockName}__item-title"><a href="${escapeHTML(post.permalink || '#')}" class="${getLinkClass(post.permalink)}">${escapeHTML(post.title || '')}</a></h3>
										${
                    post.topic && post.topic !== post.title
                      ? `<div class="${blockName}__item-topic">${escapeHTML(post.topic)}</div>`
                      : ''
                  }
										${
                    post.agency
                      ? `<div class="${blockName}__item-agency"><strong>Agency:</strong> ${escapeHTML(post.agency)}</div>`
                      : ''
                  }
										<div class="${blockName}__item-meta">
											${
                    post.due_date_formatted
                      ? `<span class="${blockName}__item-due-date"><strong>Due:</strong> ${escapeHTML(post.due_date_formatted)}</span>`
                      : ''
                  }
											${
                    post.published_date_formatted
                      ? `<span class="${blockName}__item-published-date"><strong>Published:</strong> ${escapeHTML(post.published_date_formatted)}</span>`
                      : ''
                  }
											${
                    post.finalization_date_formatted
                      ? `<span class="${blockName}__item-finalization-date"><strong>Finalized:</strong> ${escapeHTML(post.finalization_date_formatted)}</span>`
                      : ''
                  }
										</div>
									</div>
									<div class="${blockName}__item-actions">
										${
                    post.has_comment_link && post.apta_comment_link
                      ? `<a href="${escapeHTML(post.apta_comment_link)}" class="button--primary --apta-comment" target="_blank" rel="noopener">View APTA Comment</a>`
                      : ''
                  }
									</div>
								</div>
							</div>
						</article>
					`;

        default:
          return `
						<article class="${blockName}__item ${blockName}__item--resource default"
								 data-item-index="${index}"
								 data-search-terms="${post.search_terms}"
								 ${taxonomyAttrs}>
							<div class="${blockName}__item-content">
								<div class="${blockName}__item-details">
									<h3 class="${blockName}__item-title"><a href="${post.permalink}">${post.title}</a></h3>
									${
                    post.excerpt
                      ? `<div class="${blockName}__item-excerpt">${post.excerpt}</div>`
                      : ''
                  }
									${
                    post.is_member_only
                      ? `<div class="${blockName}__item-member-only">${post.is_gated ? '<a href="' + (window.aptaSSO?.loginUrl || '#') + '">Log in to access</a>' : 'Member Only'}</div>`
                      : ''
                  }
								</div>
								${
                  post.has_action_url
                    ? `
									<div class="${blockName}__item-action">
										<a href="${post.action_url}" class="${blockName}__action-button" target="_blank" rel="noopener">
											View Resource
										</a>
									</div>
								`
                    : ''
                }
							</div>
						</article>
					`;
      }
    }

    // Standards layout helpers
    function formatStandardsCount(count) {
      const value = Number(count) || 0;
      return `${value} ${value === 1 ? 'Standard' : 'Standards'}`;
    }

    /**
     * Generate HTML for a single standard in ungrouped All mode
     */
    function generateStandardsItemHTML(post, layout, index) {
      const searchTerms = escapeHTML(post.search_terms || '');
      const displayTitle = escapeHTML(post.document_title || post.title || '');
      const eyebrowLabel = escapeHTML(post.post_title || post.title || '');
      const permalink = escapeHTML(post.permalink || '#');
      const rawDate = post.latest_publication_date || post.date || '';
      const date = rawDate ? escapeHTML(rawDate) : '';
      const fileTypeLabel = post.file_type_label
        ? escapeHTML(post.file_type_label)
        : '';
      const hasActionUrl = Boolean(post.has_action_url && post.action_url);
      const actionUrl = hasActionUrl ? escapeHTML(post.action_url) : '';

      return `
        <article class="${blockName}__item ${blockName}__item--standard" data-item-index="${index}" data-search-terms="${searchTerms}">
          <div class="${blockName}__item-content">
            <div class="${blockName}__item-icon" aria-hidden="true">
              <svg width="29" height="30" viewBox="0 0 29 30" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.80551 9.41665C8.18983 9.41665 8.52257 9.26442 8.80374 8.95997C9.08464 8.65522 9.22508 8.29456 9.22508 7.87801C9.22508 7.46115 9.08436 7.1005 8.80291 6.79605C8.52174 6.4913 8.19066 6.33892 7.80967 6.33892C7.4223 6.33892 7.08817 6.49145 6.80727 6.7965C6.5261 7.10155 6.38552 7.46055 6.38552 7.8735C6.38552 8.29306 6.5261 8.65522 6.80727 8.95997C7.08817 9.26442 7.42091 9.41665 7.80551 9.41665ZM7.80551 16.5346C8.18761 16.5346 8.5198 16.3837 8.80208 16.082C9.08408 15.7802 9.22508 15.4196 9.22508 15C9.22508 14.5804 9.08436 14.2183 8.80291 13.9135C8.52174 13.6091 8.19066 13.4569 7.80967 13.4569C7.4223 13.4569 7.08817 13.6091 6.80727 13.9135C6.5261 14.2183 6.38552 14.5804 6.38552 15C6.38552 15.4196 6.52666 15.7802 6.80894 16.082C7.09122 16.3837 7.42341 16.5346 7.80551 16.5346ZM7.80551 23.6611C8.18983 23.6611 8.52257 23.5087 8.80374 23.2039C9.08464 22.8995 9.22508 22.5388 9.22508 22.122C9.22508 21.7054 9.08436 21.3448 8.80291 21.04C8.52174 20.7356 8.19066 20.5834 7.80967 20.5834C7.4223 20.5834 7.08817 20.7359 6.80727 21.0409C6.5261 21.3457 6.38552 21.7045 6.38552 22.1175C6.38552 22.5373 6.5261 22.8995 6.80727 23.2039C7.08817 23.5087 7.42091 23.6611 7.80551 23.6611ZM3.37293 30C2.58682 30 1.91772 29.7007 1.36564 29.102C0.813288 28.5036 0.537109 27.7784 0.537109 26.9263V3.07366C0.537109 2.22162 0.813288 1.49641 1.36564 0.898025C1.91772 0.299341 2.5871 0 3.37376 0H20.5451L28.2157 8.31395V26.9254C28.2157 27.7781 27.9395 28.5036 27.3871 29.102C26.8351 29.7007 26.166 30 25.3799 30H3.37293ZM3.37667 27.4772H25.3761C25.5256 27.4772 25.6483 27.4252 25.7442 27.3213C25.8402 27.2173 25.8881 27.0843 25.8881 26.9223V9.66549H19.2981V2.52276H3.37667C3.22721 2.52276 3.10451 2.57476 3.00857 2.67875C2.91263 2.78274 2.86466 2.91573 2.86466 3.07772V26.9223C2.86466 27.0843 2.91263 27.2173 3.00857 27.3213C3.10451 27.4252 3.22721 27.4772 3.37667 27.4772Z" fill="#6049E9"/></svg>
            </div>
            <div class="${blockName}__item-details">
              <div class="${blockName}__item-main-information">
                <h3 class="${blockName}__item-title"><a href="${permalink}">${displayTitle}</a></h3>
                <div class="${blockName}__item-eyebrow standards-document-tag">${eyebrowLabel}</div>
                ${date ? `<div class="${blockName}__item-date">${date}</div>` : ''}
                ${
                  fileTypeLabel
                    ? `<div class="${blockName}__item-format"><strong>Format:</strong> ${fileTypeLabel}</div>`
                    : ''
                }
              </div>
              ${
                hasActionUrl
                  ? `
                <a href="${actionUrl}" class="download button--primary" target="_blank" rel="noopener">
                  <label>Download File</label>
                  <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"><rect y="0.5" width="14" height="14" rx="2" fill="#6049E9"/><path d="M3.5 6.48211L3.9887 5.97469L6.64453 8.64791L6.64453 3L7.34604 3L7.34604 8.64791L10.0115 5.9652L10.5 6.47247L6.99536 10L3.5 6.48211Z" fill="white"/><rect x="3.5" y="11" width="7" height="0.583333" fill="white"/></svg>
                  ${fileTypeLabel ? `<span>(${fileTypeLabel})</span>` : ''}
                </a>
              `
                  : `<a href="${permalink}" class="button--primary">Details</a>`
              }
            </div>
          </div>
        </article>
      `;
    }

    function renderStandardsLayout(standardsData = {}) {
      const groups = Array.isArray(standardsData.groups)
        ? standardsData.groups
        : [];
      const parentTerm = standardsData.parent_term || null;
      const totalPosts = Number(standardsData.total_posts || 0);
      const parentTermId =
        typeof standardsData.parent_term_id !== 'undefined'
          ? standardsData.parent_term_id
          : '';

      const parentHeader = parentTerm
        ? `<header class="${blockName}__standards-header">
            <h3 class="${blockName}__standards-title">${escapeHTML(
            parentTerm.name || '',
          )}</h3>
            ${
              totalPosts
                ? `<p class="${blockName}__standards-summary">${formatStandardsCount(
                    totalPosts,
                  )}</p>`
                : ''
            }
          </header>`
        : '';

      const controls =
        groups.length > 0
          ? `<div class="${blockName}__standards-controls">
                <button class="${blockName}__standards-toggle-all"
                        type="button"
                        data-text-more="Expand All"
                        data-text-less="Collapse All"
                        aria-expanded="false">
                  <span data-toggle-label>Expand All</span>
                </button>
             </div>`
          : '';

      const groupsHtml =
        groups.length > 0
          ? groups
              .map(function (group, index) {
                return generateStandardsGroupHTML(group, index + 1);
              })
              .join('')
          : `<div class="${blockName}__no-content"><p>No standards found for this selection.</p></div>`;

      return `<div class="${blockName}__standards" data-parent-term-id="${escapeHTML(parentTermId)}">
        ${parentHeader}
        ${controls}
        <div class="${blockName}__standards-groups" data-groups-count="${groups.length}">
          ${groupsHtml}
        </div>
      </div>`;
    }

    function generateStandardsGroupHTML(group, index) {
      const groupData = group || {};
      const instanceId = instance.id || 'filtered-list';
      const groupId = `filtered-list-standard-group-${instanceId}-${index}`;
      const countValue = Number(groupData.count || 0);
      const termName = groupData.term_name
        ? escapeHTML(groupData.term_name)
        : 'Other Standards';
      const termDescription = groupData.term_description
        ? `<p class="${blockName}__standards-group-description">${escapeHTML(
            groupData.term_description,
          )}</p>`
        : '';
      const posts = Array.isArray(groupData.posts) ? groupData.posts : [];

      const itemsHtml =
        posts.length > 0
          ? posts
              .map(function (post, postIndex) {
                return generateStandardItemHTML(post, postIndex + 1, {
                  groupId,
                  groupIndex: index,
                });
              })
              .join('')
          : `<p class="${blockName}__standards-empty">No standards available in this group.</p>`;

      return `
        <section class="${blockName}__standards-group"
                 data-group-index="${index}"
                 data-term-id="${escapeHTML(groupData.term_id ?? '')}"
                 data-term-slug="${escapeHTML(groupData.term_slug ?? '')}"
                 data-count="${countValue}"
                 ${groupData.is_other ? 'data-is-other="true"' : ''}>
          <header class="${blockName}__standards-group-header">
            <div class="${blockName}__standards-group-summary">
              <h4 class="${blockName}__standards-group-title">${termName}</h4>
              <span class="${blockName}__standards-group-count">${formatStandardsCount(countValue)}</span>
              ${termDescription}
            </div>
            <button class="${blockName}__standards-group-toggle"
                    type="button"
                    data-text-more="Expand"
                    data-text-less="Collapse"
                    aria-expanded="false"
                    aria-controls="${groupId}">
              <svg width="17" height="10" viewBox="0 0 17 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.1924 0.597882L16.5 1.90675L9.3734 9.03582C9.25921 9.15073 9.12342 9.24193 8.97384 9.30417C8.82426 9.3664 8.66386 9.39844 8.50185 9.39844C8.33984 9.39844 8.17944 9.3664 8.02986 9.30417C7.88029 9.24193 7.74449 9.15073 7.6303 9.03582L0.5 1.90675L1.80763 0.599116L8.5 7.29025L15.1924 0.597882Z" fill="#6049E9"/></svg>
              <span class="visually-hidden" data-toggle-label>Expand</span>
            </button>
          </header>
          <div id="${groupId}" class="${blockName}__standards-group-panel" hidden>
            ${itemsHtml}
          </div>
        </section>
      `;
    }

    function generateStandardItemHTML(post, index, options = {}) {
      const groupId =
        options.groupId ||
        `filtered-list-standard-group-${instance.id || 'filtered-list'}-${
          options.groupIndex || 0
        }`;
      const itemId = `${groupId}-item-${index}`;
      const searchTerms = escapeHTML(post.search_terms || '');
      const title = escapeHTML(post.title || '');
      const permalink = escapeHTML(post.permalink || '#');
      const dateMarkup = post.date
        ? `<div class="${blockName}__item-date">${escapeHTML(post.date)}</div>`
        : '';
      const hasActionUrl = Boolean(post.has_action_url && post.action_url);
      const actionUrl = hasActionUrl ? escapeHTML(post.action_url) : '';
      const fileTypeLabel =
        post.file_type_label && String(post.file_type_label).length
          ? `(${escapeHTML(post.file_type_label)})`
          : '';

      return `
        <article class="${blockName}__item ${blockName}__item--standard" data-item-index="${index}" data-search-terms="${searchTerms}">
          <div class="${blockName}__item-content">
            <div class="${blockName}__item-icon" aria-hidden="true">
              <svg width="29" height="30" viewBox="0 0 29 30" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.80551 9.41665C8.18983 9.41665 8.52257 9.26442 8.80374 8.95997C9.08464 8.65522 9.22508 8.29456 9.22508 7.87801C9.22508 7.46115 9.08436 7.1005 8.80291 6.79605C8.52174 6.4913 8.19066 6.33892 7.80967 6.33892C7.4223 6.33892 7.08817 6.49145 6.80727 6.7965C6.5261 7.10155 6.38552 7.46055 6.38552 7.8735C6.38552 8.29306 6.5261 8.65522 6.80727 8.95997C7.08817 9.26442 7.42091 9.41665 7.80551 9.41665ZM7.80551 16.5346C8.18761 16.5346 8.5198 16.3837 8.80208 16.082C9.08408 15.7802 9.22508 15.4196 9.22508 15C9.22508 14.5804 9.08436 14.2183 8.80291 13.9135C8.52174 13.6091 8.19066 13.4569 7.80967 13.4569C7.4223 13.4569 7.08817 13.6091 6.80727 13.9135C6.5261 14.2183 6.38552 14.5804 6.38552 15C6.38552 15.4196 6.52666 15.7802 6.80894 16.082C7.09122 16.3837 7.42341 16.5346 7.80551 16.5346ZM7.80551 23.6611C8.18983 23.6611 8.52257 23.5087 8.80374 23.2039C9.08464 22.8995 9.22508 22.5388 9.22508 22.122C9.22508 21.7054 9.08436 21.3448 8.80291 21.04C8.52174 20.7356 8.19066 20.5834 7.80967 20.5834C7.4223 20.5834 7.08817 20.7359 6.80727 21.0409C6.5261 21.3457 6.38552 21.7045 6.38552 22.1175C6.38552 22.5373 6.5261 22.8995 6.80727 23.2039C7.08817 23.5087 7.42091 23.6611 7.80551 23.6611ZM3.37293 30C2.58682 30 1.91772 29.7007 1.36564 29.102C0.813288 28.5036 0.537109 27.7784 0.537109 26.9263V3.07366C0.537109 2.22162 0.813288 1.49641 1.36564 0.898025C1.91772 0.299341 2.5871 0 3.37376 0H20.5451L28.2157 8.31395V26.9254C28.2157 27.7781 27.9395 28.5036 27.3871 29.102C26.8351 29.7007 26.166 30 25.3799 30H3.37293ZM3.37667 27.4772H25.3761C25.5256 27.4772 25.6483 27.4252 25.7442 27.3213C25.8402 27.2173 25.8881 27.0843 25.8881 26.9223V9.66549H19.2981V2.52276H3.37667C3.22721 2.52276 3.10451 2.57476 3.00857 2.67875C2.91263 2.78274 2.86466 2.91573 2.86466 3.07772V26.9223C2.86466 27.0843 2.91263 27.2173 3.00857 27.3213C3.10451 27.4252 3.22721 27.4772 3.37667 27.4772Z" fill="#6049E9"/></svg>
            </div>
            <div class="${blockName}__item-details">
              <div class="${blockName}__item-main-information">
                <h5 class="${blockName}__item-title"><a href="${permalink}">${title}</a></h5>
                ${dateMarkup}
              </div>
              <button class="${blockName}__item-toggle"
                      data-text-more="Expand"
                      data-text-less="Collapse"
                      aria-expanded="false"
                      aria-controls="${itemId}">
                <svg width="17" height="10" viewBox="0 0 17 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.1924 0.597882L16.5 1.90675L9.3734 9.03582C9.25921 9.15073 9.12342 9.24193 8.97384 9.30417C8.82426 9.3664 8.66386 9.39844 8.50185 9.39844C8.33984 9.39844 8.17944 9.3664 8.02986 9.30417C7.88029 9.24193 7.74449 9.15073 7.6303 9.03582L0.5 1.90675L1.80763 0.599116L8.5 7.29025L15.1924 0.597882Z" fill="#6049E9"/></svg>
                <span class="visually-hidden" data-toggle-label>Expand</span>
              </button>
            </div>
          </div>
          <div id="${itemId}" class="${blockName}__item-content-extended" hidden>
            <div class="${blockName}__standard-item">
              <div class="${blockName}__standard-item-details">
                <div class="${blockName}__item-full-content">${post.rendered_content || ''}</div>
              </div>
              ${
                hasActionUrl
                  ? `<a href="${actionUrl}" class="download button--primary" target="_blank" rel="noopener">
                      <label>Download File</label>
                      <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"><rect y="0.5" width="14" height="14" rx="2" fill="#6049E9"/><path d="M3.5 6.48211L3.9887 5.97469L6.64453 8.64791L6.64453 3L7.34604 3L7.34604 8.64791L10.0115 5.9652L10.5 6.47247L6.99536 10L3.5 6.48211Z" fill="white"/><rect x="3.5" y="11" width="7" height="0.583333" fill="white"/></svg>
                      <span>${fileTypeLabel}</span>
                    </a>`
                  : ''
              }
            </div>
          </div>
        </article>
      `;
    }

    function updateToggleLabel(button, isExpanded) {
      if (!button) return;

      const moreText = button.getAttribute('data-text-more');
      const lessText = button.getAttribute('data-text-less');
      const labelTarget = button.querySelector('[data-toggle-label]');

      if (labelTarget && moreText && lessText) {
        labelTarget.textContent = isExpanded ? lessText : moreText;
      } else if (lessText && moreText && button.children.length === 0) {
        button.textContent = isExpanded ? lessText : moreText;
      }
    }

    /**
     * Update results counter
     */
    function updateResultsCounter(total) {
      if (resultsCounter) {
        resultsCounter.textContent = total;
      }
    }

    /**
     * Render pagination controls
     */
    function renderPagination(page, totalPages) {
      // Re-query DOM elements to avoid any stale reference issues
      const pgWrapper =
        paginationWrapper ||
        instance.querySelector(`.${blockName}__pagination`);
      const pgList =
        paginationList ||
        instance.querySelector(`.${blockName}__pagination-list`);

      if (!pgWrapper || !pgList) {
        console.warn(
          '[FilteredList] Pagination elements not found',
          pgWrapper,
          pgList,
        );
        return;
      }

      pgList.innerHTML = '';

      if (totalPages <= 1) {
        pgWrapper.style.display = 'none';
        return;
      }

      pgWrapper.style.display = '';

      function createPageItem(label, targetPage, isActive, isDisabled, customClass) {
        const li = document.createElement('li');
        li.className = `${blockName}__pagination-list-item`;
        if (isActive) li.classList.add('--active');
        if (isDisabled) li.classList.add('--disabled');

        const a = document.createElement('a');
        a.href = '#';
        a.className = `${blockName}__pagination-link`;
        if (customClass) a.classList.add(customClass);
        if (
          ![
            '--start-page',
            '--previous-page',
            '--next-page',
            '--end-page',
          ].includes(customClass)
        ) {
          a.textContent = label;
        }
        if (!isDisabled) {
          a.addEventListener('click', function (e) {
            e.preventDefault();
            fetchFilteredPosts(targetPage);
            instance.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }

        li.appendChild(a);
        pgList.appendChild(li);
      }
      // Create pagination controls
      createPageItem('', 1, false, page === 1, '--start-page');
      createPageItem(
        '',
        page - 1,
        false,
        page === 1,
        '--previous-page',
      );

      for (let i = 1; i <= totalPages; i++) {
        if (
          i === 1 ||
          i === totalPages ||
          (i >= page - 2 && i <= page + 2)
        ) {
          createPageItem(i.toString(), i, page === i, false, null);
        } else if (i === page - 3 || i === page + 3) {
          const li = document.createElement('li');
          li.className = `${blockName}__pagination-list-item`;
          li.innerHTML = '…';
          pgList.appendChild(li);
        }
      }

      createPageItem(
        '',
        page + 1,
        false,
        page === totalPages,
        '--next-page',
      );
      createPageItem(
        '',
        totalPages,
        false,
        page === totalPages,
        '--end-page',
      );
    }

    /**
     * Update active filter pills
     */
    function updateActiveFilters() {
      if (!activeFiltersList || !activeFiltersWrapper) return;

      activeFiltersList.innerHTML = '';

      let hasFilters = false;

      // Add search pill
      if (currentSearch) {
        hasFilters = true;
        const pill = createFilterPill('Search', currentSearch, function () {
          currentSearch = '';
          if (searchInput) searchInput.value = '';
          fetchFilteredPosts(1);
        });
        activeFiltersList.appendChild(pill);
      }

      // Add taxonomy filter pills
      Object.keys(currentFilters).forEach(function (taxonomy) {
        if (!currentFilters[taxonomy]) return;

        const select = instance.querySelector(
          `select[data-taxonomy="${taxonomy}"]`,
        );
        const value = currentFilters[taxonomy];

        const typeAllOption = select?.querySelector('option[type-all="true"]');
        if (
          typeAllOption &&
          typeAllOption.value &&
          value === typeAllOption.value
        ) {
          return;
        }

        const isMultiSelect = select?.getAttribute('data-multi') === 'true';
        const isMultiValue = value.indexOf(',') >= 0;
        const slugs = isMultiValue
          ? value.split(',').map((s) => s.trim()).filter(Boolean)
          : [value];

        slugs.forEach(function (slug) {
          hasFilters = true;
          const selectedOption = select?.querySelector(`option[value="${slug}"]`);
          const label = selectedOption ? selectedOption.textContent.trim() : slug;
          if (selectedOption && selectedOption.getAttribute('type-all') == true) {
            return;
          }
          const pill = createFilterPill(taxonomy, label, function () {
            if (isMultiSelect) {
              const arr = value.split(',').map((s) => s.trim()).filter(Boolean);
              const idx = arr.indexOf(slug);
              if (idx >= 0) arr.splice(idx, 1);
              const newValue = arr.join(',');
              currentFilters[taxonomy] = newValue || undefined;
              if (!newValue) delete currentFilters[taxonomy];
              const hiddenInput = instance.querySelector(
                `input[data-taxonomy="${taxonomy}"][data-multi="true"]`,
              );
              if (hiddenInput) hiddenInput.value = newValue || '';
            } else {
              delete currentFilters[taxonomy];
              if (select) {
                if (taxonomy === 'advocacy-type') {
                  select.value = select.options[0]?.value || '';
                  if (select.value) currentFilters[taxonomy] = select.value;
                } else {
                  select.value = '';
                }
              }
            }
            fetchFilteredPosts(1);
          });
          activeFiltersList.appendChild(pill);
        });
      });

      // Show/hide active filters wrapper
      activeFiltersWrapper.style.display = hasFilters ? 'flex' : 'none';
    }

    /**
     * Create a filter pill element
     */
    function createFilterPill(type, label, onRemove) {
      const pill = document.createElement('button');
      pill.className = `${blockName}__filter-pill`;
			pill.setAttribute('type', 'button');
			pill.setAttribute('aria-label', `Remove ${label} filter`);
      pill.innerHTML = `
				${label}
				<span class="${blockName}__filter-pill-remove"></span>
			`;

      pill.addEventListener('click', onRemove);

      return pill;
    }

    /**
     * Initialize expand/collapse functionality for awards/people layouts
     */
    function initializeExpandCollapse() {
      const items = instance.querySelectorAll(`.${blockName}__item`);
      items.forEach(function (item) {
        const toggleButton = item.querySelector(`.${blockName}__item-toggle`);
        if (!toggleButton) return;

        const contentId = toggleButton.getAttribute('aria-controls');
        // Only process toggles that have aria-controls (awards/people layouts)
        // Skip TCRP toggles which don't have aria-controls
        if (!contentId) return;

        const contentDiv = item.querySelector('#' + contentId);

        toggleButton.setAttribute('aria-expanded', 'false');
        toggleButton.classList.remove('--expanded');
        toggleButton.dataset.managedToggle = 'true';
        updateToggleLabel(toggleButton, false);

        if (contentDiv) {
          contentDiv.setAttribute('aria-hidden', 'true');
          contentDiv.setAttribute('hidden', 'hidden');
          contentDiv.classList.remove('--expanded');
        }

        toggleButton.addEventListener('click', function () {
          const willExpand =
            toggleButton.getAttribute('aria-expanded') !== 'true';
          toggleButton.classList.toggle('--expanded', willExpand);
          toggleButton.setAttribute('aria-expanded', String(willExpand));

          if (contentDiv) {
            contentDiv.classList.toggle('--expanded', willExpand);
            contentDiv.setAttribute('aria-hidden', String(!willExpand));
            if (willExpand) {
              contentDiv.removeAttribute('hidden');
            } else {
              contentDiv.setAttribute('hidden', 'hidden');
            }
          }

          updateToggleLabel(toggleButton, willExpand);
        });
      });
    }

    function initializeStandardsGroups() {
      const groups = instance.querySelectorAll(
        `.${blockName}__standards-group`,
      );
      const toggleAllButton = instance.querySelector(
        `.${blockName}__standards-toggle-all`,
      );

      if (!groups.length) {
        if (toggleAllButton) {
          toggleAllButton.setAttribute('aria-expanded', 'false');
          updateToggleLabel(toggleAllButton, false);
        }
        return;
      }

      const groupControls = [];

      function setGroupState(button, panel, expand, group) {
        button.classList.toggle('--expanded', expand);
        button.setAttribute('aria-expanded', String(expand));
        updateToggleLabel(button, expand);

        if (panel) {
          panel.classList.toggle('--expanded', expand);
          panel.setAttribute('aria-hidden', String(!expand));
          if (expand) {
            panel.removeAttribute('hidden');
          } else {
            panel.setAttribute('hidden', 'hidden');
          }
        }

        if (group) {
          group.classList.toggle('--expanded', expand);
        }
      }

      groups.forEach(function (group) {
        const button = group.querySelector(
          `.${blockName}__standards-group-toggle`,
        );
        if (!button) {
          return;
        }

        const panelId = button.getAttribute('aria-controls');
        const panel = panelId ? group.querySelector(`#${panelId}`) : null;

        button.setAttribute('aria-expanded', 'false');
        button.classList.remove('--expanded');
        button.dataset.managedToggle = 'true';
        updateToggleLabel(button, false);

        if (panel) {
          panel.setAttribute('aria-hidden', 'true');
          panel.setAttribute('hidden', 'hidden');
          panel.classList.remove('--expanded');
        }

        button.addEventListener('click', function () {
          const expand = button.getAttribute('aria-expanded') !== 'true';
          setGroupState(button, panel, expand, group);
          syncToggleAllState();
        });

        groupControls.push({ button, panel, group });
      });

      function syncToggleAllState() {
        if (!toggleAllButton) return;

        const allExpanded =
          groupControls.length > 0 &&
          groupControls.every(
            ({ button }) => button.getAttribute('aria-expanded') === 'true',
          );

        toggleAllButton.setAttribute('aria-expanded', String(allExpanded));
        updateToggleLabel(toggleAllButton, allExpanded);
      }

      if (toggleAllButton) {
        toggleAllButton.dataset.managedToggle = 'true';
        toggleAllButton.setAttribute('aria-expanded', 'false');
        updateToggleLabel(toggleAllButton, false);

        toggleAllButton.addEventListener('click', function () {
          const expand =
            toggleAllButton.getAttribute('aria-expanded') !== 'true';
          groupControls.forEach(({ button, panel, group }) => {
            setGroupState(button, panel, expand, group);
          });
          toggleAllButton.setAttribute('aria-expanded', String(expand));
          updateToggleLabel(toggleAllButton, expand);
        });
      }
    }

    /**
     * Generate HTML for a single TCRP publication item
     */
    function generateTCRPItemHTML(post, index) {
      const title = escapeHTML(post.title || '');
      const date = post.date ? escapeHTML(post.date) : '';
      const excerpt = post.excerpt || '';
      const featuredImage = post.featured_image || '';
      const tagLabel = post.tag_label ? escapeHTML(post.tag_label) : 'TCRP Publication';
      const hasActionUrl = Boolean(post.has_action_url && post.action_url);
      const actionUrl = hasActionUrl ? escapeHTML(post.action_url) : '';
      const reportNumber = post.report_number ? escapeHTML(post.report_number) : '';

      const thumbnailHtml = featuredImage
        ? `<div class="${blockName}__item-thumbnail">
            <img src="${escapeHTML(featuredImage)}" alt="${title}" loading="lazy" />
            <div class="${blockName}__item-tag">${tagLabel}</div>
          </div>`
        : `<div class="${blockName}__item-thumbnail ${blockName}__item-thumbnail--placeholder">
            <div class="${blockName}__item-tag">${tagLabel}</div>
          </div>`;

      const dateHtml = date
        ? `<div class="${blockName}__item-date">${date}</div>`
        : '';

      const reportNumberHtml = reportNumber
        ? `<div class="${blockName}__item-report-number">${reportNumber}</div>`
        : '';

      // Title link - only link if external URL exists
      const titleHtml = hasActionUrl
        ? `<a href="${actionUrl}" target="_blank" rel="noopener">${title}</a>`
        : title;

      // Expandable description - button shown only when PHP determines content is long enough
      const isExpandable = Boolean(post.is_expandable);
      const descriptionHtml = excerpt
        ? `<div class="${blockName}__item-description-wrapper">
            <div class="${blockName}__item-description ${isExpandable ? '--expandable' : ''}">${excerpt}</div>
            ${isExpandable ? `<button type="button"
                    class="${blockName}__item-toggle ${blockName}__tcrp-toggle"
                    data-text-more="Read More"
                    data-text-less="Read Less"
                    aria-expanded="false">
              Read More
            </button>` : ''}
          </div>`
        : '';

      // Action button - only show if external URL exists
      const actionButton = hasActionUrl
        ? `<div class="${blockName}__item-action">
            <a href="${actionUrl}" class="button--primary" target="_blank" rel="noopener">
              View Publication
            </a>
          </div>`
        : '';

      return `
        <article class="${blockName}__item ${blockName}__item--tcrp" data-item-index="${index}">
          <div class="${blockName}__item-content">
            ${thumbnailHtml}
            <div class="${blockName}__item-details">
              <h3 class="${blockName}__item-title">
                ${titleHtml}
              </h3>
              ${reportNumberHtml}
              ${dateHtml}
              ${descriptionHtml}
              ${actionButton}
            </div>
          </div>
        </article>
      `;
    }

    /**
     * Hide TCRP Read More toggle buttons when text doesn't actually overflow.
     * CSS line-clamp may not truncate short text, so the button would be misleading.
     */
    function hideTCRPToggleIfNotOverflowing() {
      const wrappers = contentWrapper.querySelectorAll(
        `.${blockName}__item-description-wrapper`,
      );
      wrappers.forEach(function (wrapper) {
        const desc = wrapper.querySelector(
          `.${blockName}__item-description.--expandable`,
        );
        const toggle = wrapper.querySelector(`.${blockName}__tcrp-toggle`);
        if (!desc || !toggle) return;

        // Compare scrollHeight to clientHeight to detect actual overflow
        if (desc.scrollHeight <= desc.clientHeight) {
          toggle.style.display = 'none';
        }
      });
    }

    /**
     * Generate HTML for a single Document item (AJAX pagination)
     */
    function generateDocumentItemHTML(post, index) {
      const title = escapeHTML(post.title || '');
      const date = post.date ? escapeHTML(post.date) : '';
      const excerpt = post.excerpt || '';
      const featuredImage = post.featured_image || '';
      const tagLabel = post.tag_label ? escapeHTML(post.tag_label) : 'Document';
      const hasActionUrl = Boolean(post.has_action_url && post.action_url);
      const actionUrl = hasActionUrl ? escapeHTML(post.action_url) : '';
      const isDownload = Boolean(post.is_download);
      const fileTypeLabel = post.file_type_label ? escapeHTML(post.file_type_label) : '';
      const categorySlug = post.document_category_slug ? escapeHTML(post.document_category_slug) : '';

      const imgSrc = featuredImage || '/wp-content/themes/apta/images/APTA-logo-PR.png';
      const thumbnailHtml = `<div class="${blockName}__item-thumbnail ${blockName}__item-thumbnail--portrait">
            <img src="${escapeHTML(imgSrc)}" alt="${title}" loading="lazy" />
            <div class="${blockName}__item-tag">${tagLabel}</div>
          </div>`;

      const dateHtml = date
        ? `<div class="${blockName}__item-date">${date}</div>`
        : '';

      const titleHtml = hasActionUrl
        ? `<a href="${actionUrl}" target="_blank" rel="noopener">${title}</a>`
        : title;

      const isExpandable = Boolean(post.is_expandable);
      const descriptionHtml = excerpt
        ? `<div class="${blockName}__item-description-wrapper">
            <div class="${blockName}__item-description ${isExpandable ? '--expandable' : ''}">${excerpt}</div>
            ${isExpandable ? `<button type="button"
                    class="${blockName}__item-toggle ${blockName}__document-toggle"
                    data-text-more="Read More"
                    data-text-less="Read Less"
                    aria-expanded="false">
              Read More
            </button>` : ''}
          </div>`
        : '';

      let actionButton = '';
      if (hasActionUrl) {
        if (isDownload) {
          actionButton = `<div class="${blockName}__item-action">
              <a href="${actionUrl}" class="download button--primary" target="_blank" rel="noopener">
                <label>Download</label>
                <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg"><rect y="0.5" width="14" height="14" rx="2" fill="#6049E9"/><path d="M3.5 6.48211L3.9887 5.97469L6.64453 8.64791L6.64453 3L7.34604 3L7.34604 8.64791L10.0115 5.9652L10.5 6.47247L6.99536 10L3.5 6.48211Z" fill="white"/><rect x="3.5" y="11" width="7" height="0.583333" fill="white"/></svg>
                ${fileTypeLabel ? `<span>(${fileTypeLabel})</span>` : ''}
              </a>
            </div>`;
        } else {
          actionButton = `<div class="${blockName}__item-action">
              <a href="${actionUrl}" class="button--primary" target="_blank" rel="noopener">
                View Document
              </a>
            </div>`;
        }
      }

      return `
        <article class="${blockName}__item ${blockName}__item--document"
                 data-item-index="${index}"
                 data-search-terms="${escapeHTML(post.search_terms || '')}"
                 data-category="${categorySlug}">
          <div class="${blockName}__item-content">
            ${thumbnailHtml}
            <div class="${blockName}__item-details">
              <h3 class="${blockName}__item-title">
                ${titleHtml}
              </h3>
              ${dateHtml}
              ${descriptionHtml}
              ${actionButton}
            </div>
          </div>
        </article>
      `;
    }

    /**
     * Hide Document Read More toggle buttons when text doesn't actually overflow.
     */
    function hideDocumentToggleIfNotOverflowing() {
      const wrappers = contentWrapper.querySelectorAll(
        `.${blockName}__item-description-wrapper`,
      );
      wrappers.forEach(function (wrapper) {
        const desc = wrapper.querySelector(
          `.${blockName}__item-description.--expandable`,
        );
        const toggle = wrapper.querySelector(`.${blockName}__document-toggle`);
        if (!desc || !toggle) return;

        if (desc.scrollHeight <= desc.clientHeight) {
          toggle.style.display = 'none';
        }
      });
    }



    /**
     * Rebuild a faux popup's list from its native select's current options.
     * Used when other selects may have had their options updated (e.g. after a filter change).
     * For multi-select (e.g. years), active state is read from the hidden input's comma-separated value.
     */
    function refreshFauxPopupFromSelect(select) {
      const group = select.nextElementSibling;
      if (!group || !group.classList.contains('faux-select-group')) return;
      const popup = group.querySelector('.faux-select-group--popup');
      if (!popup) return;
      const ul = popup.querySelector('ul');
      if (!ul) return;

      const blockInstance = select.closest('.filtered-list');
      const blockId = (blockInstance && blockInstance.id) || 'filtered-list';
      const taxonomy = select.getAttribute('data-taxonomy') || '';
      const hiddenInput = group.querySelector('input[data-multi="true"]');
      const isMulti = Boolean(hiddenInput);
      const selectedValues = isMulti && hiddenInput.value
        ? hiddenInput.value.split(',').map((s) => s.trim()).filter(Boolean)
        : [];

      const options = Array.from(select.options);
      ul.innerHTML = '';
      const isSortSelect = select.getAttribute('name') === 'sort';
      const optionsToShow = isSortSelect ? options : options.slice(1);
      optionsToShow.forEach(function (option) {
        const li = document.createElement('li');
        const optionValue = option.value;
        const inputId =
          blockId + '--' + taxonomy + '--' + optionValue;
        const input = document.createElement('input');
        input.type = isMulti ? 'checkbox' : 'radio';
        input.id = inputId;
        if (!isMulti) input.name = blockId + '--' + taxonomy;
        const isActive = isMulti
          ? selectedValues.includes(optionValue)
          : (option.selected && option.value !== '');
        input.checked = isActive;
        input.setAttribute('aria-label', option.textContent.trim());
        li.appendChild(input);
        const label = document.createElement('label');
        label.className = 'option' + (isActive ? ' active' : '');
        label.setAttribute('rel', optionValue);
        label.setAttribute('for', inputId);
        label.textContent = option.textContent.trim();
        li.appendChild(label);
        ul.appendChild(li);
      });

      const filterInput = popup.querySelector('input.faux-select-group--filter');
      if (filterInput && filterInput.value) {
        filterInput.dispatchEvent(new Event('input', { bubbles: false }));
      }

      const toggle = group.querySelector('.faux-select-group--toggle');
      const totalSpan = toggle?.querySelector('.total');
      if (totalSpan) {
        const count = popup.querySelectorAll('label.option.active').length;
        totalSpan.textContent = String(count);
        totalSpan.hidden = count === 0;
      }

      const selectName = select.getAttribute('name');
      const usesSelectionAsLabel = !isMulti && (selectName === 'sort' || selectName === 'filter-name');
      if (usesSelectionAsLabel && toggle && toggle.firstChild && toggle.firstChild.nodeType === Node.TEXT_NODE) {
        const selectedOpt = options[select.selectedIndex] || options[0];
        toggle.firstChild.nodeValue = selectedOpt.textContent.trim();
      }
    }

    /**
     * Refresh all other faux-select popups in the same filter dropdowns container
     * so they reflect their respective select's current options (e.g. after one filter changed).
     * Scoped to the same .filtered-list block only.
     */
    function refreshAllSiblingFauxPopups(currentSelect) {
      const blockInstance = currentSelect.closest('.filtered-list');
      if (!blockInstance) return;
      const container = blockInstance.querySelector(
        `.${blockName}__filter-dropdowns`,
      );
      if (!container) return;
      const allSelects = container.querySelectorAll(
        `.${blockName}__filter-select.faux-select-native-hidden`,
      );
      allSelects.forEach(function (otherSelect) {
        if (otherSelect === currentSelect) return;
        refreshFauxPopupFromSelect(otherSelect);
      });
    }

    /**
     * Refresh every faux-select popup in this block instance from its native select.
     * Call after AJAX completes if the server or other code updated select options.
     */
    function refreshAllFauxPopupsInInstance() {
      if (!instance) return;
      const allSelects = instance.querySelectorAll(
        `.${blockName}__filter-select.faux-select-native-hidden`,
      );
      allSelects.forEach(function (select) {
        refreshFauxPopupFromSelect(select);
      });
    }

    /**
     * Create a faux dropdown that wraps a native select: hide the select,
     * show a button + popup. Choosing an option updates the select and triggers change.
     * When data-multi="true" (e.g. years), allows multiple selection and stores value in a hidden input.
     */
    function createFauxDropdownForSelect(select) {
      const options = Array.from(select.options);
      if (options.length === 0) return;

      const taxonomy = select.getAttribute('data-taxonomy') || '';
      const isMulti = select.getAttribute('data-multi') === 'true';
      const selectName = select.getAttribute('name');
      const isSortSelect = selectName === 'sort';
      // Selects whose toggle label should reflect the current selection
      // (no badge counter, single-select). Their `.total` span is hidden via CSS.
      const usesSelectionAsLabel = !isMulti && (selectName === 'sort' || selectName === 'filter-name');
      const fixedLabel = usesSelectionAsLabel
        ? (options[select.selectedIndex] || options[0]).textContent.trim()
        : options[0].textContent.trim();
      const optionsToShow = isSortSelect ? options : options.slice(1);

      const group = document.createElement('div');
      group.className = 'faux-select-group';

      if (isMulti) {
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.setAttribute('data-taxonomy', taxonomy);
        hiddenInput.setAttribute('data-multi', 'true');
        hiddenInput.value = currentFilters[taxonomy] || '';
        group.appendChild(hiddenInput);
      }

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'faux-select-group--toggle';
      toggle.setAttribute('aria-haspopup', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.appendChild(document.createTextNode(fixedLabel));
      const totalSpan = document.createElement('span');
      totalSpan.className = 'total';
      toggle.appendChild(totalSpan);

      const popup = document.createElement('div');
      popup.className = 'faux-select-group--popup faux-select-group--popup--' + (isMulti ? 'multi-select' : 'single-select');
      popup.hidden = true;

      const filterInput = document.createElement('input');
      filterInput.type = 'text';
      filterInput.className = 'faux-select-group--filter';
      filterInput.setAttribute('placeholder', 'Type to filter');
      filterInput.setAttribute('aria-label', 'Filter options');
      filterInput.addEventListener('input', function () {
        const q = (filterInput.value || '').trim().toLowerCase();
        const items = popup.querySelectorAll('ul li');
        items.forEach(function (li) {
          const label = li.querySelector('label.option');
          const text = label ? label.textContent.trim().toLowerCase() : '';
          li.style.display = q === '' || text.indexOf(q) >= 0 ? '' : 'none';
        });
      });
      filterInput.addEventListener('click', function (e) {
        e.stopPropagation();
      });
      popup.appendChild(filterInput);

      const ul = document.createElement('ul');
      const hiddenInputForBuild = isMulti ? group.querySelector('input[data-multi="true"]') : null;
      const selectedForBuild = isMulti && hiddenInputForBuild && hiddenInputForBuild.value
        ? hiddenInputForBuild.value.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
      const blockId = (instance && instance.id) || 'filtered-list';
      optionsToShow.forEach(function (option) {
        const li = document.createElement('li');
        const optionValue = option.value;
        const inputId = blockId + '--' + taxonomy + '--' + optionValue;
        const input = document.createElement('input');
        input.type = isMulti ? 'checkbox' : 'radio';
        input.id = inputId;
        if (!isMulti) input.name = blockId + '--' + taxonomy;
        const isActive = isMulti
          ? selectedForBuild.includes(optionValue)
          : (option.selected && option.value !== '');
        input.checked = isActive;
        input.setAttribute('aria-label', option.textContent.trim());
        li.appendChild(input);
        const label = document.createElement('label');
        label.className = 'option' + (isActive ? ' active' : '');
        label.setAttribute('rel', optionValue);
        label.setAttribute('for', inputId);
        label.textContent = option.textContent.trim();
        li.appendChild(label);
        ul.appendChild(li);
      });
      const listWrap = document.createElement('div');
      listWrap.className = 'faux-select-group--popup--list-wrap';
      listWrap.appendChild(ul);
      popup.appendChild(listWrap);
      group.appendChild(toggle);
      group.appendChild(popup);

      totalSpan.textContent = String(popup.querySelectorAll('label.option.active').length);
      totalSpan.hidden = popup.querySelectorAll('label.option.active').length === 0;

      let multiValueOnOpen = '';

      function close(fetchAfter) {
        toggle.setAttribute('aria-expanded', 'false');
        popup.hidden = true;
        if (isMulti && fetchAfter) {
          const hiddenInput = group.querySelector('input[data-multi="true"]');
          fetchAfter = hiddenInput && hiddenInput.value !== multiValueOnOpen;
        }
        if (fetchAfter && typeof fetchFilteredPosts === 'function') {
          fetchFilteredPosts(1);
        }
      }

      function open() {
        const blockInstance = group.closest('.filtered-list');
        if (blockInstance) {
          blockInstance.querySelectorAll('.faux-select-group').forEach(function (otherGroup) {
            if (otherGroup === group) return;
            const otherToggle = otherGroup.querySelector('.faux-select-group--toggle');
            const otherPopup = otherGroup.querySelector('.faux-select-group--popup');
            if (otherToggle && otherToggle.getAttribute('aria-expanded') === 'true' && otherPopup) {
              otherToggle.setAttribute('aria-expanded', 'false');
              otherPopup.hidden = true;
            }
          });
        }
        toggle.setAttribute('aria-expanded', 'true');
        popup.hidden = false;
        if (isMulti) {
          const hiddenInput = group.querySelector('input[data-multi="true"]');
          multiValueOnOpen = hiddenInput ? hiddenInput.value : '';
        }
      }

      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          close(false);
        } else {
          open();
        }
      });

      popup.addEventListener('click', function (e) {
        let label = e.target.closest('label.option');
        if (!label && e.target.matches('input[type="radio"], input[type="checkbox"]')) {
          const li = e.target.closest('li');
          label = li ? li.querySelector('label.option') : null;
        }
        if (!label) return;
        e.preventDefault();
        const value = label.getAttribute('rel');

        if (isMulti) {
          const hiddenInput = group.querySelector('input[data-multi="true"]');
          if (!hiddenInput) return;
          if (value === '') return;
          let arr = hiddenInput.value ? hiddenInput.value.split(',').map((s) => s.trim()).filter(Boolean) : [];
          const idx = arr.indexOf(value);
          if (idx >= 0) {
            arr.splice(idx, 1);
          } else {
            arr.push(value);
          }
          const newValue = arr.join(',');
          hiddenInput.value = newValue;
          if (newValue) {
            currentFilters[taxonomy] = newValue;
          } else {
            delete currentFilters[taxonomy];
          }
          refreshAllSiblingFauxPopups(select);
          refreshFauxPopupFromSelect(select);
          if (typeof fetchFilteredPosts === 'function') {
            fetchFilteredPosts(1);
          }
        } else {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          refreshAllSiblingFauxPopups(select);
          refreshFauxPopupFromSelect(select);
          close();
        }
      });

      document.addEventListener('click', function closeOnOutside(e) {
        if (group.contains(e.target)) return;
        const blockInstance = group.closest('.filtered-list');
        if (!blockInstance || !blockInstance.contains(e.target)) return;
        if (toggle.getAttribute('aria-expanded') !== 'true') return;
        close(false);
      });

      select.classList.add('faux-select-native-hidden');
      select.insertAdjacentElement('afterEnd', group);
    }

    /**
     * Initialize event listeners
     */
    function initializeEventListeners() {
      // Search functionality
      if (searchForm && searchInput) {
        searchForm.addEventListener('submit', function (e) {
          e.preventDefault();
          currentSearch = searchInput.value.trim();
          if (enableDebug)
            console.log(`[FilteredList] Search: "${currentSearch}"`);
          fetchFilteredPosts(1);
        });
      }




      // Filter event listeners
      filterSelects.forEach(function (select) {
        createFauxDropdownForSelect(select);

        select.addEventListener('change', function (e) {
          const taxonomy = e.target.getAttribute('data-taxonomy');
          if (taxonomy) {
            if (e.target.value) {
              currentFilters[taxonomy] = e.target.value;
            } else {
              delete currentFilters[taxonomy];
            }
            if (enableDebug)
              console.log(
                `[FilteredList] Filter ${taxonomy}:`,
                currentFilters[taxonomy],
              );
            fetchFilteredPosts(1);
          }
        });
      });

      // Sort functionality
      if (sortSelect) {
        sortSelect.addEventListener('change', function (e) {
          currentSort = e.target.value;
          if (enableDebug) console.log(`[FilteredList] Sort: ${currentSort}`);
          fetchFilteredPosts(1);
        });
      }

      // Remove all filters
      if (removeAllFiltersButton) {
        removeAllFiltersButton.addEventListener('click', function (e) {
          e.preventDefault();

          // Reset all filters
          currentFilters = {};
          currentSearch = '';
          currentSort = '1';

          // Reset form elements
          if (searchInput) searchInput.value = '';
          filterSelects.forEach((select) => {
            const taxonomy = select.getAttribute('data-taxonomy');

            if (taxonomy == 'advocacy-type') {
              select.value = select.options[0]?.value || '';
            } else {
              select.value = '';
            }
          });
          if (sortSelect) sortSelect.value = '1';

          // Clear multi-select hidden inputs (e.g. years)
          const multiInputs = instance.querySelectorAll('input[data-multi="true"]');
          multiInputs.forEach((input) => {
            input.value = '';
          });

          // Clear faux dropdown filter inputs (search-in-popup) and reset list visibility
          const filterInputs = instance.querySelectorAll('.faux-select-group--filter');
          filterInputs.forEach((input) => {
            input.value = '';
            input.dispatchEvent(new Event('input', { bubbles: false }));
          });

          // Rebuild faux popup lists so active state and options are in sync
          refreshAllFauxPopupsInInstance();

          updateCurrentFiltersFromSelects();

          // Clear active filter pills immediately
          updateActiveFilters();

          fetchFilteredPosts(1);

          if (enableDebug) console.log(`[FilteredList] All filters reset`);
        });
      }
    }

    // Check if jQuery is available
    if (typeof jQuery === 'undefined' && typeof window.$ === 'undefined') {
      console.error('[FilteredList] jQuery is not available');
      return;
    }

    // Use global jQuery
    const $ = window.jQuery || window.$;

    // Add nonce to global scope for AJAX
    if (!window.wpAjax) {
      window.wpAjax = {
        ajaxurl: window.ajaxurl || '/wp-admin/admin-ajax.php',
        nonce: 'filtered_list_nonce_placeholder', // This should be localized from PHP
      };
    }

    function openTextBlock() {
      document.addEventListener('click', function (e) {
        // Check if the clicked element or its parent is a .filtered-list__item-toggle
        const button = e.target.closest('.filtered-list__item-toggle');
        if (button && button.dataset.managedToggle !== 'true') {
          const expandable = button.previousElementSibling;

          if (expandable) {
            expandable.classList.toggle('open');
          }

          const moreText = button.dataset.textMore || 'Read More';
          const lessText = button.dataset.textLess || 'Read Less';
          const isExpanded = button.getAttribute('aria-expanded') === 'true';

          if (!isExpanded) {
            button.textContent = lessText;
            button.setAttribute('aria-expanded', 'true');
          } else {
            button.textContent = moreText;
            button.setAttribute('aria-expanded', 'false');
          }
        }
      });
    }

    function updateCurrentFiltersFromSelects() {
      // Reset currentFilters
      currentFilters = {};

      // Loop through all filter selects
      filterSelects.forEach(function (select) {
        const taxonomy = select.getAttribute('data-taxonomy');
        if (!taxonomy) return;
        if (taxonomy === 'current_cpt') return;

        const group = select.nextElementSibling;
        const hiddenInput = group && group.classList.contains('faux-select-group')
          ? group.querySelector('input[data-multi="true"]')
          : null;

        const value = hiddenInput
          ? hiddenInput.value.trim()
          : select.value;

        if (value) {
          if (!hiddenInput) {
            const selectedOption = select.options[select.selectedIndex];
            if (
              selectedOption &&
              selectedOption.getAttribute('type-all') === 'true'
            ) {
              return;
            }
          }
          currentFilters[taxonomy] = value;
        }
      });

      if (enableDebug) {
        console.log('[FilteredList] Updated currentFilters:', currentFilters);
      }
    }

    // Initialize the block
    initializeEventListeners();
    openTextBlock();
    initializeStandardsGroups();
    initializeExpandCollapse();

    // Hide TCRP Read More buttons for text that doesn't overflow
    if (layout === 'tcrp') {
      hideTCRPToggleIfNotOverflowing();
    }

    // Don't load initial content via AJAX - use server-rendered content
    // Only use AJAX for filtering, searching, and pagination
  });
}
